import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

interface DateRangePickerProps {
  onDateRangeChange: (startDate: string | null, endDate: string | null) => void;
  className?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ onDateRangeChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<string>('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const predefinedRanges = [
    { key: 'all', label: 'Toutes les données', value: null },
    { key: 'today', label: "Aujourd'hui", value: 0 },
    { key: 'week', label: '7 derniers jours', value: 7 },
    { key: 'month', label: '30 derniers jours', value: 30 },
    { key: 'quarter', label: '3 derniers mois', value: 90 },
    { key: 'year', label: 'Cette année', value: 365 },
    { key: 'custom', label: 'Période personnalisée', value: 'custom' }
  ];

  const handleRangeSelect = (range: any) => {
    setSelectedRange(range.key);
    
    if (range.key === 'all') {
      onDateRangeChange(null, null);
    } else if (range.key === 'custom') {
      // Don't close dropdown for custom range
      return;
    } else if (typeof range.value === 'number') {
      const endDate = new Date();
      const startDate = new Date();
      
      if (range.value === 0) {
        // Today
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
      } else if (range.value === 365) {
        // This year
        startDate.setMonth(0, 1);
        startDate.setHours(0, 0, 0, 0);
      } else {
        // Days ago
        startDate.setDate(startDate.getDate() - range.value);
        startDate.setHours(0, 0, 0, 0);
      }
      
      onDateRangeChange(
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0]
      );
    }
    
    if (range.key !== 'custom') {
      setIsOpen(false);
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      onDateRangeChange(customStartDate, customEndDate);
      setIsOpen(false);
    }
  };

  const getCurrentLabel = () => {
    const range = predefinedRanges.find(r => r.key === selectedRange);
    return range?.label || 'Sélectionner une période';
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <Calendar className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">{getCurrentLabel()}</span>
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-2">
            {predefinedRanges.map((range) => (
              <button
                key={range.key}
                onClick={() => handleRangeSelect(range)}
                className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 ${
                  selectedRange === range.key ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>

          {selectedRange === 'custom' && (
            <div className="border-t border-gray-200 p-4">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Date de début
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="flex-1 px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleCustomDateApply}
                    disabled={!customStartDate || !customEndDate}
                    className="flex-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Appliquer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;